import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { fetchPreviewBlob, triggerDownload } from '@/services/resource-asset/resource-asset.service';
import type { ResourceAssetItem, ResourceHistoryItem } from '../../shared/types';
import { trackGAEvent } from '@/lib/ga-custom-events/dispatcher';

interface PreviewModalResource {
  documentId: string;
  title: string;
  type?: {
    type: string;
  } | null;
  category?: {
    category: string;
  } | null;
  resourceHistory?: ResourceHistoryItem[] | null;
}

interface PreviewModalProps {
  resource: PreviewModalResource | null;
  asset: ResourceAssetItem | null;
  assets?: ResourceAssetItem[] | null;
  onAssetChange?: (index: number) => void;
  isOpen: boolean;
  onClose: () => void;
  onPreviewStatusChange?: (documentId: string, previewStatus: 'unavailable') => void;
}

export const usePreviewModal = ({
  resource,
  asset,
  assets,
  onAssetChange,
  isOpen,
  onClose,
  onPreviewStatusChange,
}: PreviewModalProps) => {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const containerRef = useRef<HTMLDivElement>(null);

  const MIN_ZOOM = 0.25;
  const MAX_ZOOM = 4;
  const ZOOM_STEP = 0.25;

  const showSidebar = useMemo(() => {
    return assets && assets.length > 1;
  }, [assets]);

  const toggleSidebar = useCallback(() => {
    setIsSidebarOpen((prev) => !prev);
  }, []);

  const closeSidebar = useCallback(() => {
    setIsSidebarOpen(false);
  }, []);

  const handleClose = useCallback(() => {
    setIsLoading(true);
    setHasError(false);
    setErrorMessage(null);
    setZoom(1);
    setPosition({ x: 0, y: 0 });
    if (objectUrl) {
      URL.revokeObjectURL(objectUrl);
      setObjectUrl(null);
    }
    onClose();
  }, [onClose, objectUrl]);

  const handleZoomIn = useCallback(() => {
    setZoom((prev) => Math.min(prev + ZOOM_STEP, MAX_ZOOM));
  }, []);

  const handleZoomOut = useCallback(() => {
    setZoom((prev) => {
      const newZoom = Math.max(prev - ZOOM_STEP, MIN_ZOOM);
      if (newZoom <= 1) {
        setPosition({ x: 0, y: 0 });
      }
      return newZoom;
    });
  }, []);

  const handleZoomReset = useCallback(() => {
    setZoom(1);
    setPosition({ x: 0, y: 0 });
  }, []);

  const handleWheel = useCallback((e: React.WheelEvent) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -ZOOM_STEP : ZOOM_STEP;
    setZoom((prev) => {
      const newZoom = Math.min(Math.max(prev + delta, MIN_ZOOM), MAX_ZOOM);
      if (newZoom <= 1) {
        setPosition({ x: 0, y: 0 });
      }
      return newZoom;
    });
  }, []);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      setIsDragging(true);
      setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
    },
    [position],
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (!isDragging) return;
      e.preventDefault();
      setPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y,
      });
    },
    [isDragging, dragStart],
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      if (e.touches.length === 1) {
        e.preventDefault();
        const touch = e.touches[0];
        setIsDragging(true);
        setDragStart({ x: touch.clientX - position.x, y: touch.clientY - position.y });
      }
    },
    [position],
  );

  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      if (!isDragging || e.touches.length !== 1) return;
      e.preventDefault();
      const touch = e.touches[0];
      setPosition({
        x: touch.clientX - dragStart.x,
        y: touch.clientY - dragStart.y,
      });
    },
    [isDragging, dragStart],
  );

  const handleTouchEnd = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleDocumentClick = useCallback(() => {
    if (showSidebar && isSidebarOpen) {
      closeSidebar();
    }
  }, [showSidebar, isSidebarOpen, closeSidebar]);

  const handleAssetClick = useCallback(
    (index: number) => {
      if (onAssetChange) {
        onAssetChange(index);
      }
    },
    [onAssetChange],
  );

  const handleDownload = useCallback(async () => {
    if (!asset?.documentId) return;
    setIsDownloading(true);
    try {
      await triggerDownload(asset.documentId);
      const latestResourceVersion = [...(resource?.resourceHistory || [])]?.sort(
        (a, b) => Number(b.version) - Number(a.version),
      )?.[0]?.version;
      const latestAssetVersion = [...(asset.resourceAssetHistory || [])]?.sort(
        (a, b) => Number(b.version) - Number(a.version),
      )?.[0]?.version;
      trackGAEvent('resource_asset_downloaded', {
        resource_name: resource?.title ?? '',
        resource_type: resource?.type?.type ?? '',
        resource_id: resource?.documentId ?? '',
        resource_category: resource?.category?.category ?? '',
        resource_version: latestResourceVersion ? Number(latestResourceVersion) : 0,
        resource_asset_version: latestAssetVersion ? Number(latestAssetVersion) : 0,
        resource_asset_id: asset.documentId,
      });
    } catch (error) {
      console.error('Download failed:', error);
    } finally {
      setIsDownloading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asset?.documentId]);

  useEffect(() => {
    if (!isOpen || !asset?.documentId) {
      return;
    }

    let isCancelled = false;

    const loadPreview = async () => {
      try {
        setIsLoading(true);
        setHasError(false);
        setErrorMessage(null);

        const { blob } = await fetchPreviewBlob(asset.documentId);

        if (isCancelled) return;

        const url = URL.createObjectURL(blob);
        setObjectUrl(url);
        setZoom(1);
        setPosition({ x: 0, y: 0 });
        setIsSidebarOpen(true);
        setIsLoading(false);

        const viewedResourceVersion = [...(resource?.resourceHistory || [])]?.sort(
          (a, b) => Number(b.version) - Number(a.version),
        )?.[0]?.version;
        const viewedAssetVersion = [...(asset.resourceAssetHistory || [])]?.sort(
          (a, b) => Number(b.version) - Number(a.version),
        )?.[0]?.version;
        trackGAEvent('resource_asset_viewed', {
          resource_name: resource?.title ?? '',
          resource_type: resource?.type?.type ?? '',
          resource_id: resource?.documentId ?? '',
          resource_category: resource?.category?.category ?? '',
          resource_version: viewedResourceVersion ? Number(viewedResourceVersion) : 0,
          resource_asset_version: viewedAssetVersion ? Number(viewedAssetVersion) : 0,
          resource_asset_id: asset.documentId,
        });
      } catch (error) {
        if (isCancelled) return;
        console.error('Error loading preview:', error);

        const errorMsg = error instanceof Error ? error.message : 'Failed to load preview.';
        setErrorMessage(errorMsg);
        setHasError(true);
        setIsLoading(false);
      }
    };

    loadPreview();

    return () => {
      isCancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, asset?.documentId, asset?.previewStatus, onPreviewStatusChange]);

  useEffect(() => {
    return () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [objectUrl]);

  return {
    MIN_ZOOM,
    MAX_ZOOM,
    ZOOM_STEP,
    isLoading,
    hasError,
    objectUrl,
    errorMessage,
    isDownloading,
    zoom,
    containerRef,
    isDragging,
    position,
    showSidebar,
    isSidebarOpen,
    handleDownload,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    handleMouseLeave,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    handleWheel,
    handleZoomOut,
    handleZoomReset,
    handleZoomIn,
    handleClose,
    handleAssetClick,
    handleDocumentClick,
    toggleSidebar,
    closeSidebar,
  };
};
